import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getUserList } from '@/services/userService';
import {
  getClusterList,
  getClusterType,
  getKafkaTopics,
  getRoleList,
  getNamespace,
} from '@/services/organizeService';

const initialState = {
  loading: false,
  userListOptions: [],
  clusterList: [],
  roleList: [],
  namespaceTree: [],
  kafkaTopicList: [],
  clusterTypeOptions: [],
};

export const fetchClusterType = createAsyncThunk('platform/fetchClusterType', async () => {
  const response = await getClusterType();
  return response.data;
});

export const fetchUserListOptions = createAsyncThunk('organize/fetchUserListOptions', async () => {
  const { data } = await getUserList({ page: 1, limit: 99999 });
  return data;
});

export const fetchClusterList = createAsyncThunk('organize/fetchClusterList', async () => {
  const response = await getClusterList();
  return response.data;
});

export const fetchRoleList = createAsyncThunk('organize/fetchRoleList', async (params) => {
  const response = await getRoleList(params);
  return response.data;
});

export const fetchKafkaTopics = createAsyncThunk('organize/fetchKafkaTopics', async (params) => {
  const response = await getKafkaTopics(params);
  return response.data;
});

export const fetchNamespace = createAsyncThunk('organize/fetchNamespace', async (params) => {
  const response = await getNamespace(params);
  return response.data;
});

export const organizeSlice = createSlice({
  name: 'organize',
  initialState,
  reducers: {
    resetOptions: (state) => {
      state.roleList = [];
      state.namespaceTree = [];
      state.kafkaTopicList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserListOptions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserListOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.userListOptions = action.payload?.map((item) => ({
          label: item.account,
          value: item.id,
        }));
      })
      .addCase(fetchUserListOptions.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchClusterList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClusterList.fulfilled, (state, action) => {
        state.loading = false;
        state.clusterList = action.payload?.map((item) => ({
          label: item.clusterName,
          value: item.clusterName,
          clusterType: item.clusterType,
        }));
      })
      .addCase(fetchClusterList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchRoleList.pending, (state) => {
        state.roleList = [];
        state.loading = true;
      })
      .addCase(fetchRoleList.fulfilled, (state, action) => {
        state.loading = false;
        state.roleList = action.payload.roleList?.map((item) => ({
          label: item,
          value: item,
        }));
      })
      .addCase(fetchRoleList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchNamespace.pending, (state) => {
        state.loading = true;
        state.namespaceTree = [];
      })
      .addCase(fetchNamespace.fulfilled, (state, action) => {
        state.loading = false;
        const tempObject = {};
        const treeData = [];
        (action.payload.namespaceList || [])
          .map((item) => item.split('/'))
          .forEach((item) => {
            const [tenant, namespace] = item;
            tempObject[tenant] = tempObject[tenant] || [];
            tempObject[tenant].push(namespace);
          });

        for (const [tenant, namespaceArray] of Object.entries(tempObject)) {
          treeData.push({
            label: tenant,
            value: tenant,
            children: namespaceArray.map((item) => ({
              label: item,
              value: `${tenant}/${item}`,
            })),
          });
        }

        state.namespaceTree = treeData;
      })
      .addCase(fetchNamespace.rejected, (state) => {
        state.loading = false;
        state.namespaceTree = [];
      })
      .addCase(fetchKafkaTopics.pending, (state) => {
        state.loading = true;
        state.kafkaTopicList = [];
      })
      .addCase(fetchKafkaTopics.fulfilled, (state, action) => {
        state.loading = false;
        state.kafkaTopicList = action.payload.kafkaTopicList?.map((item) => ({
          label: item,
          value: item,
        }));
      })
      .addCase(fetchKafkaTopics.rejected, (state) => {
        state.loading = false;
        state.kafkaTopicList = [];
      })
      .addCase(fetchClusterType.fulfilled, (state, action) => {
        state.clusterTypeOptions = action.payload.map((item) => ({
          label: item,
          value: item,
        }));
      });
  },
});

export default organizeSlice.reducer;

export const { resetOptions } = organizeSlice.actions;
